<template>
  <div class="credit-payments-tab">
    <!-- Summary Cards -->
    <div v-if="loading" class="text-center py-5">
      <span class="spinner-border text-primary" role="status"></span>
      <p class="mt-2 text-muted small">Loading credit data…</p>
    </div>
    <template v-else>
      <div class="credit-card-wrapper mb-4">
        <div class="credit-kpi-card">
          <p>Total Credit Purchases (Unpaid)</p>
          <strong class="credit-orange">{{ summary.totalUnpaid }}</strong>
        </div>
        <div class="credit-kpi-card">
          <p class="credit-red">Overdue Payments</p>
          <strong class="credit-red">{{ summary.overdue }}</strong>
        </div>
        <div class="credit-kpi-card">
          <p>Due Within 1 Week</p>
          <strong class="credit-red">{{ summary.dueInWeek }}</strong>
        </div>
        <div class="credit-kpi-card">
          <p>Due Within 1 Month</p>
          <strong class="credit-orange">{{ summary.dueInMonth }}</strong>
        </div>
        <div class="credit-kpi-card">
          <p>Due Within 2 Months</p>
          <strong class="credit-blue">{{ summary.dueInTwoMonths }}</strong>
        </div>
        <div class="credit-kpi-card">
          <p>Due Within 3 Months</p>
          <strong class="credit-blue">{{ summary.dueInThreeMonths }}</strong>
        </div>
      </div>

      <!-- Chart -->
      <div class="credit-chart-card mb-4">
        <h6 class="fw-bold mb-3">Credit Obligations by Due Month (Next 6 Months)</h6>
        <div v-if="chartData.labels.length === 0" class="text-muted text-center py-4">
          No upcoming credit obligations found.
        </div>
          <Bar v-else-if="chartData.datasets && chartData.datasets.length" :data="chartData" :options="chartOptions" style="max-height: 320px;" />
      </div>

      <!-- Detail Table -->
      <div class="credit-chart-card">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="fw-bold mb-0">Credit Purchase Details</h6>
          <select class="form-select form-select-sm" v-model="filterBucket" style="width: auto;">
            <option value="all">All Unpaid</option>
            <option value="overdue">Overdue</option>
            <option value="week">Due This Week</option>
            <option value="month">Due This Month</option>
            <option value="twomonths">Due in 2 Months</option>
            <option value="threemonths">Due in 3 Months</option>
          </select>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm">
            <thead>
              <tr>
                <th>PO No.</th>
                <th>Amount</th>
                <th>Delivery Date</th>
                <th>Credit Period</th>
                <th>Due Date</th>
                <th>Days Remaining</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredDetails.length === 0">
                <td colspan="8" class="text-center text-muted">No records found</td>
              </tr>
              <tr v-for="p in filteredDetails" :key="p.id" :class="rowClass(p)">
                <td>{{ p.purchaseNo }}</td>
                <td>{{ formatMoney(p.amount) }}</td>
                <td>{{ formatDate(p.deliveryDate) }}</td>
                <td>{{ p.creditPeriod != null ? p.creditPeriod + ' days' : '—' }}</td>
                <td>{{ formatDate(p.dueDate) }}</td>
                <td><span :class="daysClass(p.daysRemaining)">{{ daysLabel(p.daysRemaining) }}</span></td>
                <td>
                  <span v-if="p.isPaid" class="badge bg-success">Paid</span>
                  <span v-else class="badge bg-danger">Unpaid</span>
                </td>
                <td>
                  <a :href="'/purchases/pdf/' + p.id" class="btn btn-sm btn-default" style="border:1px solid #000; font-size:11px;">View</a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend)

export default {
  name: 'CreditPaymentsTab',
  components: { Bar },

  data() {
    return {
      token: JSON.parse(localStorage.getItem('access_token')),
      loading: false,
      summary: {
        totalUnpaid: 0,
        overdue: 0,
        dueInWeek: 0,
        dueInMonth: 0,
        dueInTwoMonths: 0,
        dueInThreeMonths: 0,
      },
      rawDetails: [],
      filterBucket: 'all',
      chartData: { labels: [], datasets: [] },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                if (ctx.datasetIndex === 1) {
                  return ` Amount: KES ${new Intl.NumberFormat('en-US').format(ctx.raw)}`
                }
                return ` Count: ${ctx.raw}`
              },
            },
          },
        },
        scales: {
          y: { beginAtZero: true, ticks: { stepSize: 1 } },
          y1: {
            beginAtZero: true,
            position: 'right',
            grid: { drawOnChartArea: false },
            ticks: {
              callback: (v) => 'KES ' + new Intl.NumberFormat('en-US', { notation: 'compact' }).format(v),
            },
          },
        },
      },
    }
  },

  computed: {
    filteredDetails() {
      return this.rawDetails.filter((p) => {
        const days = p.daysRemaining
        if (this.filterBucket === 'overdue') return days !== null && days < 0
        if (this.filterBucket === 'week')    return days !== null && days >= 0 && days <= 7
        if (this.filterBucket === 'month')   return days !== null && days >= 0 && days <= 30
        if (this.filterBucket === 'twomonths')   return days !== null && days >= 0 && days <= 60
        if (this.filterBucket === 'threemonths') return days !== null && days >= 0 && days <= 90
        return true
      })
    },
  },

  mounted() {
    this.fetchCreditAnalytics()
  },

  methods: {
    formatMoney(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        currencyDisplay: 'code',
        maximumFractionDigits: 0,
      }).format(Number(val || 0))
    },

    formatDate(val) {
      if (!val) return '—'
      try {
        return new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      } catch {
        return val
      }
    },

    daysLabel(days) {
      if (days === null) return 'No due date'
      if (days < 0)  return `${Math.abs(days)}d overdue`
      if (days === 0) return 'Due today'
      return `${days}d remaining`
    },

    daysClass(days) {
      if (days === null) return 'text-muted'
      if (days < 0)   return 'text-danger fw-bold'
      if (days <= 7)  return 'text-danger'
      if (days <= 30) return 'text-warning fw-bold'
      return 'text-success'
    },

    rowClass(p) {
      const d = p.daysRemaining
      if (d === null) return ''
      if (d < 0)   return 'table-danger'
      if (d <= 7)  return 'table-warning'
      return ''
    },

    async fetchCreditAnalytics() {
      this.loading = true
      try {
        const res = await fetch(process.env.VUE_APP_URL + '/purchases/credit-analytics', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + this.token },
        })
        const data = await res.json()
        if (data.status) {
          this.summary = data.summary
          await this.fetchCreditDetails()
          const buckets = data.chartData || []
          this.chartData = {
            labels: buckets.map((b) => b.label),
            datasets: [
              {
                label: '# Purchases Due',
                data: buckets.map((b) => b.count),
                backgroundColor: 'rgba(54, 162, 235, 0.7)',
                borderColor: 'rgba(54, 162, 235, 1)',
                borderWidth: 1,
                yAxisID: 'y',
              },
              {
                label: 'Total Amount (KES)',
                data: buckets.map((b) => b.amount),
                backgroundColor: 'rgba(255, 159, 64, 0.5)',
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1,
                yAxisID: 'y1',
              },
            ],
          }
        }
      } catch (e) {
        console.error('Failed to load credit analytics', e)
      } finally {
        this.loading = false
      }
    },

    async fetchCreditDetails() {
      try {
        const res = await fetch(process.env.VUE_APP_URL + '/purchases', {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + this.token },
        })
        const data = await res.json()
        const all = data.purchases || []
        const now = new Date()
        const msDay = 1000 * 60 * 60 * 24
        this.rawDetails = all
          .filter((p) => p.isCredit && !p.isPaid)
          .map((p) => {
            let dueDate = null
            let daysRemaining = null
            if (p.deliveryDate && p.creditPeriod != null) {
              dueDate = new Date(p.deliveryDate)
              dueDate.setDate(dueDate.getDate() + Number(p.creditPeriod))
              daysRemaining = Math.ceil((dueDate.getTime() - now.getTime()) / msDay)
            }
            return { ...p, dueDate, daysRemaining }
          })
          .sort((a, b) => (a.daysRemaining ?? Infinity) - (b.daysRemaining ?? Infinity))
      } catch (e) {
        console.error('Failed to fetch credit details', e)
      }
    },
  },
}
</script>

<style scoped>
.credit-payments-tab {
  padding: 4px 0;
}

.credit-card-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.credit-kpi-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: rgba(49, 32, 104, 0.12) 0 2px 8px 0;
  padding: 16px 20px;
  min-width: 160px;
  flex: 1;
}

.credit-kpi-card p {
  margin: 0 0 6px 0;
  font-size: 12px;
  color: #666;
}

.credit-kpi-card strong {
  font-size: 22px;
}

.credit-chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: rgba(49, 32, 104, 0.12) 0 2px 8px 0;
  padding: 20px 24px;
}

.credit-blue   { color: #1a6eb5; }
.credit-orange { color: #e08c00; }
.credit-red    { color: #c0392b; }
</style>
